import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Paper,
  CircularProgress, Chip, LinearProgress, IconButton, Tooltip
} from '@mui/material';
import {
  Business, ConfirmationNumber, Schedule, Warning,
  CheckCircle, Error, Refresh, TrendingUp, GetApp
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { toast } from 'react-toastify';

const KCOverview = () => {
  const [loading, setLoading] = useState(true);
  const [kcStats, setKcStats] = useState([]);
  const [totalStats, setTotalStats] = useState({
    total: 0,
    open: 0,
    inProgress: 0,
    closed: 0
  });
  const navigate = useNavigate();

  const fetchKCStatistics = async () => {
    try {
      setLoading(true);
      const response = await api.get('/kcs/statistics');

      if (response.data.success) {
        setKcStats(response.data.data);

        const totals = response.data.data.reduce((acc, kc) => {
          acc.total += kc.totalTickets || 0;
          acc.open += kc.openTickets || 0;
          acc.inProgress += kc.inProgressTickets || 0;
          acc.closed += kc.closedTickets || 0;
          return acc;
        }, { total: 0, open: 0, inProgress: 0, closed: 0 });

        setTotalStats(totals);
      }
    } catch (error) {
      console.error('Fehler beim Laden der KC-Statistiken:', error);
      toast.error('Fehler beim Laden der KC-Statistiken');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKCStatistics();
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'open':
        return '#ef4444';
      case 'in_progress':
        return '#f59e0b';
      case 'closed':
        return '#10b981';
      case 'cancelled':
        return '#dc2626';
      default:
        return '#6b7280';
    }
  };

  const getProgressValue = (kc) => {
    if (kc.totalTickets === 0) return 0;
    return (kc.closedTickets / kc.totalTickets) * 100;
  };

  const handleKCClick = async (kcId, kcName) => {
    try {
      // Zeige Lade-Indikator
      toast.info(`Erstelle PDF für ${kcName}...`);

      // Hole Token aus localStorage
      const token = localStorage.getItem('token');

      // Lade PDF vom Backend
      const response = await fetch(`${api.defaults.baseURL}/kcs/${kcId}/tickets/pdf`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Fehler beim Erstellen der PDF');
      }

      // Konvertiere Response zu Blob
      const blob = await response.blob();

      // Erstelle Download-Link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Extrahiere Filename aus Content-Disposition Header oder verwende Default
      const contentDisposition = response.headers.get('content-disposition');
      const fileNameMatch = contentDisposition && contentDisposition.match(/filename="(.+)"/);
      const fileName = fileNameMatch ? fileNameMatch[1] : `KC_${kcName}_Tickets.pdf`;

      link.download = fileName;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('PDF wurde heruntergeladen!');
    } catch (error) {
      console.error('Fehler beim PDF-Download:', error);
      toast.error('Fehler beim Erstellen der PDF');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, background: 'linear-gradient(135deg, #CC0000 0%, #8B0000 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', mb: 1 }}>
            KC-Übersicht
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Übersicht aller KCs und ihrer offenen Tickets
          </Typography>
        </Box>
        <Tooltip title="Aktualisieren">
          <IconButton onClick={fetchKCStatistics} sx={{ ml: 2 }}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={6} md={3}>
          <Card sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <ConfirmationNumber sx={{ mr: 1 }} />
                <Typography variant="h6">Gesamt</Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>
                {totalStats.total}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Tickets insgesamt
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} md={3}>
          <Card sx={{ backgroundColor: '#fef2f2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Warning sx={{ color: '#ef4444', mr: 1 }} />
                <Typography variant="h6" sx={{ color: '#7f1d1d' }}>Offen</Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700, color: '#ef4444' }}>
                {totalStats.open}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Offene Tickets
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} md={3}>
          <Card sx={{ backgroundColor: '#fef3c7' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Schedule sx={{ color: '#f59e0b', mr: 1 }} />
                <Typography variant="h6" sx={{ color: '#78350f' }}>In Bearbeitung</Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700, color: '#f59e0b' }}>
                {totalStats.inProgress}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Tickets in Bearbeitung
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} md={3}>
          <Card sx={{ backgroundColor: '#f0fdf4' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <CheckCircle sx={{ color: '#10b981', mr: 1 }} />
                <Typography variant="h6" sx={{ color: '#14532d' }}>Geschlossen</Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700, color: '#10b981' }}>
                {totalStats.closed}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Geschlossene Tickets
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
        KC-Details
      </Typography>

      <Grid container spacing={3}>
        {kcStats.map((kc) => (
          <Grid item xs={12} md={6} lg={4} key={kc.id}>
            <Card
              sx={{
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4
                }
              }}
              onClick={() => handleKCClick(kc.id, kc.name)}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Business sx={{ mr: 1, color: '#6366f1' }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
                    {kc.name}
                  </Typography>
                  <Tooltip title="PDF herunterladen">
                    <GetApp sx={{ color: '#6366f1', fontSize: 20 }} />
                  </Tooltip>
                </Box>

                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" sx={{ color: '#ef4444', fontWeight: 700 }}>
                        {kc.openTickets || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Offen
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" sx={{ color: '#f59e0b', fontWeight: 700 }}>
                        {kc.inProgressTickets || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        In Bearbeitung
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" sx={{ color: '#10b981', fontWeight: 700 }}>
                        {kc.closedTickets || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Geschlossen
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                <Box sx={{ mb: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Fortschritt
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {Math.round(getProgressValue(kc))}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={getProgressValue(kc)}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: '#e2e8f0',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 4,
                        background: 'linear-gradient(90deg, #6366f1 0%, #a78bfa 100%)'
                      }
                    }}
                  />
                </Box>

                {kc.highPriorityTickets > 0 && (
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mt: 2,
                    p: 1,
                    backgroundColor: '#fef2f2',
                    borderRadius: 1
                  }}>
                    <Error sx={{ color: '#ef4444', fontSize: 20, mr: 1 }} />
                    <Typography variant="caption" sx={{ color: '#7f1d1d' }}>
                      {kc.highPriorityTickets} Tickets mit hoher Priorität
                    </Typography>
                  </Box>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Gesamt: {kc.totalTickets || 0} Tickets
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {kcStats.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Business sx={{ fontSize: 48, color: '#9ca3af', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Keine KCs gefunden
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Es wurden noch keine KCs angelegt.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default KCOverview;